/**
 * liveProjection — should this run project its sections into the live Word
 * document as they are drafted?
 *
 * Deliberately NOT the same thing as ``insertionMode``. That flag is overloaded
 * across unrelated lifecycle behaviours we must not disturb:
 *
 *   - ``useRunControl`` auto-hydrates (a ``forceClear`` body wipe + full
 *     replay) on restore when the mode is not "batch".
 *   - ``run_bundle.effective_insertion_mode`` records it on the run record.
 *
 * Only the remaining consumer — the scaffold in ``useWordWriteQueue`` — is about
 * projection. This predicate drives that one, leaving the mode at "batch"
 * everywhere so restore and run metadata are untouched.
 */

import type { DocTypeConfig } from "../api/client";

/**
 * True when a run on this doc-type should scaffold the skeleton and receive
 * per-section live writes.
 *
 * Live projection is the DEFAULT for every doc-type. The doc-type's
 * ``generation.insertion_mode`` is deliberately NOT consulted: it used to gate
 * this, which left CER, CTD_M2 and CTD_M5 drafting into the server's assembled
 * .docx only — a writer watching those runs saw a scaffold that never filled
 * and had to press "Load doc" to see any content at all.
 *
 * Dropping that gate costs nothing server-side, because the add-in already
 * pins ``insertion_mode_override: "batch"`` on EVERY run it starts
 * (``useDraftFlow.actuallyStartRun``). The server therefore still assembles the
 * full .docx for download on every doc-type exactly as before; the YAML's
 * ``insertion_mode`` no longer reaches any live decision. Projection and
 * assembly are not alternatives — a live-projected run gets both.
 *
 * The two remaining guards are preconditions, not preferences: each marks a
 * case where a live write is a guaranteed no-op or a data-loss risk, so
 * neither is a doc-type opting out of live projection.
 *
 * @param config          the doc-type config; leaf-scoping is the CALLER's job
 *                        (the skeleton it scaffolds must already be narrowed).
 * @param submissionMode  the run's submission mode; "custom" opts out.
 */
export function shouldLiveProject(
	config: DocTypeConfig | null | undefined,
	submissionMode: string | null | undefined,
): boolean {
	if (!config) return false;
	// Custom submissions draft into the writer's OWN document: there is no
	// doctype skeleton to scaffold, and a custom module's synthetic ``CUSTOM-N``
	// section number matches no heading, so a live write would no-op anyway.
	// Their regen/audit re-projection goes through the customSectionNumber /
	// customSectionTitle overrides instead.
	if (submissionMode === "custom") return false;
	// Nothing to scaffold — without headings every section write is a silent
	// no-op, so claiming live projection would only mislead the spine tracker.
	if ((config.skeleton?.length ?? 0) === 0) return false;
	return true;
}
